import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, Observable, map, takeUntil } from 'rxjs';
import { Select } from '@ngxs/store';

import { AngularMaterialModule } from '@app/shared/modules/angular-material.module';
import { DynamicPipe } from '@app/shared/pipes/dynamic-pipe';
import { AppDataState } from '@app/shared/stores/app-data.state';
import { CommonFieldName, NgxsActionName, WebApiRoute } from '@app/shared/constants';
import { HttpMethodEnum, Status } from '@app/shared/enums';
import { BasedComponent } from '@shared/components/based.component';
import { TeamMessageFieldName } from '@app/pages/team-message/team-message.constant';
import { WebApiResponse } from '@app/shared/classes/general';
import { SubRegion } from '@app/shared/classes/subRegion';
import { SubRegionNamesTranslate } from '@app/shared/pipes/sub-region-names-translate';
import { TerminalNamesTranslate } from '@app/shared/pipes/terminal-names-translate';
import { Terminal } from '@app/shared/classes/terminal';
import { ReceiverTerminalListComponent } from '@app/pages/team-message/receiver-terminal-list/receiver-terminal-list.component';
import { CountryCodeTranslate } from '@app/shared/pipes/country-code-translate';
import { ReceiverDriverListComponent } from '@app/pages/team-message/receiver-driver-list/receiver-driver-list.component';
import { WebApiService } from '@app/shared/services/web-api.service';
import { Utilities } from '@app/shared/utils/utilities';

@Component({
  selector: 'app-team-message-data-grid-cell',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [AngularMaterialModule, CommonModule, TranslateModule, DynamicPipe, CountryCodeTranslate, SubRegionNamesTranslate, TerminalNamesTranslate],
  styleUrls: ['./team-message-data-grid-cell.component.css'],
  template: `
  @if(data[field] === ''){
    <div></div>
  }
  @else {
    @switch (field) {
      @case (teamMessageFieldName.status){
        @if(data[field]===status.INACTIVE) {
        <mat-checkbox disabled [checked]="true"></mat-checkbox>
        }
      }
      @case (teamMessageFieldName.targetOrganization){
        @if(data['countries'] && data['countries'].length>0){
          <div class="team-message-country-cell" [innerHtml]="data['countries'][0] | countryCodeTranslate"></div>
        }
        @else if(data['subRegion_Ids'] && data['subRegion_Ids'].length>0){
          <div class="team-message-region-cell" [innerHtml]="data['subRegion_Ids'] | subRegionNamesTranslate: (subRegions$ | async).data"></div>
        }
        @else if(data['terminal_Ids'] && data['terminal_Ids'].length<=2 && data['terminal_Ids'].length>0){
          <div [innerHtml]="data['terminal_Ids'] | terminalNamesTranslate: (terminals$ | async)"></div>
        }
        @else if(data['terminal_Ids'] && data['terminal_Ids'].length>2){
          <span [innerHtml]="data['terminal_Ids'] | terminalNamesTranslate: (terminals$ | async): 2"></span>
          <a href="javascript:void(0);" (click)="onTerminalColumnOpen()">
            {{"ml_144130" | translate}}
          </a>
        }
        @else {
          <div></div>
        }
      }
      @case (teamMessageFieldName.driver_Ids){
        <a href="javascript:void(0);" (click)="onDriverColumnOpen()">
          {{"ml_144128" | translate}}
        </a>
      }
      @case (commonFieldName.attachment){
        <a href="javascript:void(0);" (click)="onClick()">
          {{displayText$ | async | translate}}
        </a>
      }
      @default {
        <div [innerHtml]="data[field] | dynamicPipe: column.pipe: column.pipeArgs"></div>
      }
    }
  }
  `
})
export class TeamMessageDataGridCell extends BasedComponent {
  dialog = inject(MatDialog);
  webApiService = inject(WebApiService);

  @Input() data: any;
  @Input() field: string;
  @Input() column: any;

  status = Status;
  teamMessageFieldName = TeamMessageFieldName;
  commonFieldName = CommonFieldName;

  isProcessingSub = new BehaviorSubject(false);
  isProcessing$ = this.isProcessingSub.asObservable();
  displayText$: Observable<string>;

  @Select(AppDataState.taskResponse(NgxsActionName.GET_SUB_REGIONS)) subRegions$: Observable<WebApiResponse<SubRegion[]>>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_DRIVER_RELATED_TERMINAL_LIST)) terminals$: Observable<Terminal[]>;

  constructor() { super(); }

  override ngOnInit() {
    super.ngOnInit();
    this.displayText$ = this.isProcessing$.pipe(map(p => p ? "ml_144035" : "ml_143965"));
  }

  onTerminalColumnOpen = () => {
    this.dialog.open(ReceiverTerminalListComponent, {
      width: '700px',
      disableClose: true,
      data: this.data[CommonFieldName.terminal_Ids]
    });
  }

  onDriverColumnOpen = () => {
    this.dialog.open(ReceiverDriverListComponent, {
      width: '80%',
      disableClose: true,
      data: this.data
    });
  }

  onClick = () => {
    this.isProcessingSub.next(true);
    var id = this.data[this.teamMessageFieldName.teamMessage_Id];
    var fileName = this.data[this.commonFieldName.attachment];

    this.webApiService.requestRawData(HttpMethodEnum.GET, true, `${WebApiRoute.TEAM_MESSAGE}/${id}/attachment`, null, null, "arraybuffer")
      .pipe(map(res => Utilities.BinaryFileDownload(res, fileName)), takeUntil(this.destroy$))
      .subscribe(res => {
        this.isProcessingSub.next(false);
      });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.isProcessingSub.complete();
  }
}
